import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const http = axios.create({
  baseURL: API,
  timeout: 20000,
});

/**
 * Backend bazen HTTPException(status, {code, message, detail}) dict olarak
 * fırlatır. `toast.error(detailDict)` React'e doğrudan obje render ettirir →
 * "Objects are not valid as a React child" → ErrorBoundary yoksa tüm
 * sayfa beyaza döner. Bu helper string'e indirger.
 */
export const errMsg = (e, fallback = "Hata") => {
  const d = e?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (d && typeof d === "object") {
    return d.message || d.msg || d.error || JSON.stringify(d);
  }
  return e?.response?.data?.message || e?.message || fallback;
};

export const api = {
  prices: (symbol = "ETHUSDT") => http.get(`/prices/${symbol}`).then((r) => r.data),
  meta: () => http.get("/meta/eligible-pairs").then((r) => r.data),
  positions: (params) => http.get("/positions", { params }).then((r) => r.data),
  createPosition: (payload) => http.post("/positions", payload).then((r) => r.data),
  closePosition: (id, payload) =>
    http.post(`/positions/${id}/close`, payload).then((r) => r.data),
  deletePosition: (id) => http.delete(`/positions/${id}`).then((r) => r.data),
  killSwitch: ({ confirm, reason }) =>
    http.post(`/positions/kill-switch`, { confirm, reason }).then((r) => r.data),
  dailyPnl: () => http.get(`/positions/daily-pnl`).then((r) => r.data),
  stats: () => http.get("/stats").then((r) => r.data),
  setupStatus: () => http.get("/setup-status").then((r) => r.data),
  getSettings: () => http.get("/settings").then((r) => r.data),
  updateSettings: (payload) => http.put("/settings", payload).then((r) => r.data),
  currentSignal: (symbol = "ETHUSDT") =>
    http.get(`/signal/current`, { params: { symbol } }).then((r) => r.data),
  scanSignal: () => http.post("/signals/scan").then((r) => r.data),
  listSignals: (limit = 30) =>
    http.get("/signals", { params: { limit } }).then((r) => r.data),
  respondSignal: (id, answer) =>
    http.post(`/signals/${id}/respond`, { answer }).then((r) => r.data),
  // Order-flow signal engine
  signalsLive: (params = {}) =>
    http.get("/signals/live", { params }).then((r) => r.data),
  analyzeSymbol: (symbol, params = {}) =>
    http.get(`/signals/analyze/${symbol}`, { params }).then((r) => r.data),
  scanFlow: (min_confidence = 50) =>
    http.post(`/signals/scan-flow`, null, { params: { min_confidence } })
        .then((r) => r.data),
  openNow: (body) =>
    http.post(`/signals/open-now`, body).then((r) => r.data),
  balances: () => http.get("/balances").then((r) => r.data),
  exchangePositions: () => http.get("/exchange-positions").then((r) => r.data),
  // Borsa pozisyon operasyonları (canlı emirler)
  exClose: (body) => http.post("/exchange-ops/close", body).then((r) => r.data),
  exClosePair: (body) =>
    http.post("/exchange-ops/close-pair", body).then((r) => r.data),
  exTpsl: (body) => http.post("/exchange-ops/tpsl", body).then((r) => r.data),
  exHedgeTpsl: (body) =>
    http.post("/exchange-ops/hedge-tpsl", body).then((r) => r.data),
  exLeverage: (body) =>
    http.post("/exchange-ops/leverage", body).then((r) => r.data),
  // Kâr Planlayıcı
  planProjection: (body) =>
    http.post("/plan/projection", body).then((r) => r.data),
  planExecute: (body) => http.post("/plan/execute", body).then((r) => r.data),
  planSetDailyTarget: (target) =>
    http.post("/plan/daily-target", { target_usd: target }).then((r) => r.data),
  planDailyProgress: () =>
    http.get("/plan/daily-progress").then((r) => r.data),
  klines: (symbol = "ETHUSDT", interval = "minute15", size = 96) =>
    http.get(`/klines/${symbol}`, { params: { interval, size } }).then((r) => r.data),
  spreadHistory: (symbol = "ETHUSDT", hours = 24) =>
    http.get(`/spread-history/${symbol}`, { params: { hours } }).then((r) => r.data),
  // AI Assistant
  aiAnalyzeUrl: (video_url, user_hint = "") =>
    http.post("/ai/analyze-video-url", { video_url, user_hint }).then((r) => r.data),
  aiAnalyzeUpload: (file) => {
    const fd = new FormData();
    fd.append("file", file);
    return http.post("/ai/analyze-video-upload", fd, {
      headers: { "Content-Type": "multipart/form-data" },
    }).then((r) => r.data);
  },
  aiListStrategies: () => http.get("/ai/strategies").then((r) => r.data),
  aiDeleteStrategy: (id) => http.delete(`/ai/strategies/${id}`).then((r) => r.data),
  aiActivateStrategy: (id, active) =>
    http.post(`/ai/strategies/${id}/activate`, { active }).then((r) => r.data),
  aiJournalReport: (days = 14) =>
    http.post("/ai/journal-report", { days }).then((r) => r.data),
  aiJournalLatest: () => http.get("/ai/journal-report/latest").then((r) => r.data),
  // Loss Recovery Program
  lrTiers: () => http.get("/loss-recovery/tiers").then((r) => r.data),
  lrTierCalc: (body) =>
    http.post("/loss-recovery/tier-calc", body).then((r) => r.data),
  lrSummary: () => http.get("/loss-recovery/summary").then((r) => r.data),
  positionEligibility: (id) =>
    http.get(`/positions/${id}/eligibility`).then((r) => r.data),
  positionLive: (id) =>
    http.get(`/positions/${id}/live`).then((r) => r.data),
  addMargin: (id, leg, amount_usdt) =>
    http.post(`/positions/${id}/add-margin`, { leg, amount_usdt }).then((r) => r.data),
  // Spot → Futures transfer
  transferToFutures: (exchange, amount_usdt) =>
    http.post(`/exchanges/transfer-to-futures`, { exchange, amount_usdt })
        .then((r) => r.data),
  // Health probe (public, cache'li — UI banner)
  healthProbe: () => http.get(`/health/probe`).then((r) => r.data),
  // Admin freeze
  getFreeze: () => http.get(`/admin/freeze`).then((r) => r.data),
  setFreeze: (active, reason) =>
    http.post(`/admin/freeze`, { active, reason }).then((r) => r.data),
  // Execution engine — order book, slippage, pre-execute check
  execStatus: () => http.get(`/execution/status`).then((r) => r.data),
  execBook: (exchange, symbol, levels = 10) =>
    http.get(`/execution/book/${exchange}/${symbol}`,
             { params: { levels } }).then((r) => r.data),
  execQuote: (exchange, symbol, side, qty) =>
    http.get(`/execution/quote`,
             { params: { exchange, symbol, side, qty } })
        .then((r) => r.data),
  execCheck: (exchange, symbol, side, qty) =>
    http.get(`/execution/check-execution`,
             { params: { exchange, symbol, side, qty } })
        .then((r) => r.data),
};
